import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import {DevicesRemoveActionAliases, FragmentNames} from '../../../codegen';
import {Inject} from '@odinjs/odin';
import { DynamicFieldsAPI, FieldAPI, FieldsetAPI, FormAPI, InputAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import moment from 'moment';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import {RemovedReasonType} from './device-remove-types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../app/authentication';
import { ActionType, DevicesViewerType, ReasonType } from '../commons/register-device';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableSubmitEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import timeService from '../../../services/time-service';
import { DevicesDynamicForm } from '../commons/devices-dynamic-form';

enum DeviceType {
  DEVICE_TYPE = 'R',
}

@Controller({ component: DevicesRemoveActionAliases.LAYOUT_DEVICES_REMOVE_ACTION, fragment: FragmentNames.DEVICES_REMOVE_ACTION })
class DevicesRemoveActionController extends BaseController {

  @Inject(DevicesRemoveActionAliases.FORM_DEVICES_REMOVE_ACTION_VIEWER)
  formDeviceRemoveAPI!: FormAPI;

  @Inject(DevicesRemoveActionAliases.FILTER_FIELD_REASON_REMOVE)
  filterFieldReasonRemoveAPI!: FieldAPI;

  @Inject(DevicesRemoveActionAliases.FIELDSET_DYNAMIC_FIELDS)
  fieldsetDynamicAPI!: FieldsetAPI;

  @Inject(DevicesRemoveActionAliases.DYNAMIC_FIELDS)
  dynamicFieldAPI!: DynamicFieldsAPI;

  @Inject(DevicesRemoveActionAliases.FILTER_FIELD_REMOVE_ACTI)
  fieldRemoveActionAPI!: FieldAPI;

  @Inject(DevicesRemoveActionAliases.INPUT_DATE)
  setupDateInputApi!: InputAPI;

  private getParamRemoveAction = (deviceSequence: number): Promise<boolean> => {
    const params = {
      deviceSequence: deviceSequence,
    }
    return HttpService.get(Endpoint.GET_REMOVED_REASON, params)
      .then((res) => {
        const verifyRemovedReason = res.body as RemovedReasonType;
        return verifyRemovedReason.requiredDescription === 'S';
      })
  }

  private returnDevicesList(): void {
    this.historyService.back();
    applicationContext.popNavigationItem();
  }

  private submitRemove(body: Record<string, Value>): void {
    HttpService.post(Endpoint.DEVICES_REGISTER, undefined, { body })
      .then(() => {
        this.showToast('text.device-removed', ToastLevel.SUCCESS);
        this.returnDevicesList();
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  @OnMount(DevicesRemoveActionAliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields') as Metadata;
    this.dynamicFieldAPI.setMetadata(dynamicFields);
  }

  private handleDynamicFields(): void {
    const {deviceSequence} = this.historyService.getCurrentRouteParams();
    const deviceType = DeviceType.DEVICE_TYPE;

    DevicesDynamicForm.handleDynamicFields(this.fieldsetDynamicAPI, deviceSequence, deviceType, this.context);
  }

  @OnBeforeSubmit({ target: DevicesRemoveActionAliases.FORM_DEVICES_REMOVE_ACTION_VIEWER })
  handleBeforeSubmitForm(event: AbortableSubmitEvent): void {
    event.abort();
    const formInfos = DevicesDynamicForm.createFormPattern(event);
    const formData = this.formDeviceRemoveAPI.getRecord();
    const {patientEncounterDeviceId, encounterId} = this.historyService.getCurrentRouteParams();
    const devSeq = this.context.get('deviceSequence');
    const actionRemove = formData.actionRemove as ActionType;
    const reasonRemoval = formData.reasonRemoval as ReasonType;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formatDateTime = moment(formData.date as string).utc().format(ISO_FULL_DATE_FORMAT);
    const observation = formData.observation as string;
    const body = {
      actionDeviceSequence: actionRemove?.internalProcedureSequence,
      bronchoaspirationStatus: 'N',
      deviceAttributes: formInfos,
      deviceSequence: devSeq,
      encounterId: encounterId as number,
      ensStatus: DeviceType.DEVICE_TYPE,
      eventDescription: observation,
      optionStatus: 'R',
      professionalCode: user.physicalPersonId,
      registerDate: formatDateTime,
      registerSequence: patientEncounterDeviceId as number,
      releasedStatus:'S',
      removedReasonSequence: reasonRemoval?.reasonRemovedSequence,
      removeStatusDischarge: 'N',
      statusDeviceOption: 'P',
      successStatus: 'S',
      unconsciousStatus: 'N',
    };
    this.submitRemove(body);
  }

  @OnMount({ target: DevicesRemoveActionAliases.FORM_DEVICES_REMOVE_ACTION_VIEWER })
  handleMountLayoutDevicesRemove(): void {
    const {patientEncounterDeviceId, patientId, encounterId} = this.historyService.getCurrentRouteParams();
    timeService.getCurrentDateTime().then(dateMoment => {
      this.setupDateInputApi.setValue(dateMoment)
      HttpService.get(Endpoint.GET_DEVICES_VIEWER, {
        encounterId,
        patientEncounterDeviceId,
        patientId
      }).then((response) => {
        const res = response.body as DevicesViewerType;
        const deviceDescription = res.deviceDescription;
        const deviceSequence = res.deviceSequence;
        this.context.set({
          deviceSequence,
          encounterId: encounterId,
          patientId: patientId,
        });
        this.formDeviceRemoveAPI.setData({date: dateMoment, deviceDescription});
        this.handleDynamicFields();
      });
    });
  }

  @OnChange({ target: DevicesRemoveActionAliases.FILTER_FIELD_REMOVE_ACTI })
  handleChangeRemoveAction(): void {
    const devSeq = this.context.get('deviceSequence');

    this.getParamRemoveAction(devSeq).then(isRemoveReason => {
      const validator: Validator = { name: 'required', options: {}};

      this.context.set({
        removedReason: isRemoveReason,
      });

      if (isRemoveReason) {
        this.filterFieldReasonRemoveAPI.addValidator(validator);
        this.filterFieldReasonRemoveAPI.validate();
      }
      this.filterFieldReasonRemoveAPI.removeValidator('required');
    });
  }

  @OnChange({ target: DevicesRemoveActionAliases.AUTOC_REASON_REMOVE_ITEM })
  async handleChangeAutocompleteReason(): Promise<void> {
    const param = this.context.get('removedReason');
    const validator: Validator = { name: 'required', options: {}};
    if (param) {
      this.filterFieldReasonRemoveAPI.addValidator(validator);
      await this.filterFieldReasonRemoveAPI.validate();
    }
    await this.filterFieldReasonRemoveAPI.removeValidator('required');
  }

  @OnCancel({ target: DevicesRemoveActionAliases.FORM_DEVICES_REMOVE_ACTION_VIEWER })
  handleCancelForm(): void {
    this.returnDevicesList();
  }

  @OnAfterFetch({ target: DevicesRemoveActionAliases.FETCH_REMOVE_ACTION_ITEM})
  handleAfterFetch(event: FetchEvent): void {
    const validator: Validator = { name: 'required', options: {}};
    const resArray = event.data as Record<string, string>[];
    if (event && resArray.length > 0) {
      this.fieldRemoveActionAPI.addValidator(validator);
      this.fieldRemoveActionAPI.validate();
      return;
    }
    this.fieldRemoveActionAPI.removeValidator('required');
  }

}

export default DevicesRemoveActionController;
